
import { SET_FILMSTRIP_ENABLED } from '../../filmstrip/actionTypes';
import { shouldRestrictFocusModeView } from '../../focus-mode/functions';
import { SELECT_LARGE_VIDEO_PARTICIPANT } from '../../large-video/actionTypes';
import { APP_STATE_CHANGED } from '../../mobile/background/actionTypes';
import { SET_PIP_ACTIVE } from '../../mobile/picture-in-picture/actionTypes';
import { SCREEN_SHARE_REMOTE_PARTICIPANTS_UPDATED, SET_TILE_VIEW } from '../../video-layout/actionTypes';
import { shouldDisplayTileView } from '../../video-layout/functions';
import { SET_AUDIO_ONLY } from '../audio-only/actionTypes';
import { CONFERENCE_JOINED } from '../conference/actionTypes';
import {
    PARTICIPANT_JOINED,
    PARTICIPANT_KICKED,
    PARTICIPANT_LEFT
} from '../participants/actionTypes';
import {
    getParticipantById,
    getParticipantCount
} from '../participants/functions';
import { MiddlewareRegistry } from '../redux';
import { TRACK_ADDED, TRACK_REMOVED } from '../tracks/actionTypes';
import { getVideoTrackByParticipant, isLocalVideoTrackDesktop } from '../tracks/functions';

import { limitLastN } from './functions';
import logger from './logger';
import { getOnScreenTiles, setOnScreenTilesListener } from './onScreenTiles';

const LASTN_COALESCE_DELAY_MS = 120;
let _lastNUpdateTimer;

/**
 * The video selection (on-stage and selected sources) last sent to the
 * bridge, so an unchanged selection is not re-sent.
 */
let _lastSelectedSources;

/**
 * How long the web page must stay hidden before remote video is paused. A quick alt-tab, or the moment between a tab
 * switch and the Picture-in-Picture window opening, should not stop and restart every remote stream.
 */
const PAGE_HIDDEN_LASTN_DELAY_MS = 3000;

/**
 * Whether the web conference has been out of sight long enough to stop receiving remote video. The web equivalent of
 * the mobile app going to the background (features/background, which only exists on mobile).
 */
let _pageHidden = false;
let _pageHiddenTimer;

/**
 * Whether the conference cannot be seen: the page is hidden and it is not being shown in a Document
 * Picture-in-Picture window either (the main tab is hidden while the user watches the PiP window).
 *
 * @returns {boolean}
 */
function _isConferenceOutOfSight() {
    return document.visibilityState === 'hidden' && !window.documentPictureInPicture?.window;
}

/**
 * Follows page visibility on web so that remote video is paused while nobody can see it. Decoding every remote video
 * in a hidden tab competes with audio for CPU, and when the OS throttles background browsers (Windows Energy Saver,
 * efficiency mode) that is what made the audio choppy until the tab was brought back.
 *
 * @param {Store} store - The redux store.
 * @returns {void}
 */
function _followPageVisibility(store) {
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
        return; // React Native: the app state (features/background) covers this.
    }

    const onChange = () => {
        clearTimeout(_pageHiddenTimer);
        _pageHiddenTimer = undefined;

        if (!_isConferenceOutOfSight()) {
            if (_pageHidden) {
                _pageHidden = false;
                _flushLastNUpdate(store);
            }

            return;
        }

        _pageHiddenTimer = setTimeout(() => {
            _pageHiddenTimer = undefined;

            if (_isConferenceOutOfSight() && !_pageHidden) {
                _pageHidden = true;
                _flushLastNUpdate(store);
            }
        }, PAGE_HIDDEN_LASTN_DELAY_MS);
    };

    document.addEventListener('visibilitychange', onChange);

    // Opening or closing the Picture-in-Picture window changes whether the conference is on screen without a
    // visibility change of the main page, and whether tiles are selected by visibility (see _updateLastN).
    window.documentPictureInPicture?.addEventListener?.('enter', event => {
        onChange();
        _scheduleLastNUpdate(store);
        event?.window?.addEventListener?.('pagehide', () => setTimeout(() => {
            onChange();
            _scheduleLastNUpdate(store);
        }, 0));
    });
}

/**
 * Schedules a debounced lastN recomputation.
 *
 * @param {Store} store - The redux store.
 * @returns {void}
 */
function _scheduleLastNUpdate(store) {
    if (_lastNUpdateTimer) {
        clearTimeout(_lastNUpdateTimer);
    }

    _lastNUpdateTimer = setTimeout(() => {
        _lastNUpdateTimer = undefined;
        _updateLastN(store);
    }, LASTN_COALESCE_DELAY_MS);
}

/**
 * Flushes any pending lastN update and recomputes immediately.
 *
 * @param {Store} store - The redux store.
 * @returns {void}
 */
function _flushLastNUpdate(store) {
    if (_lastNUpdateTimer) {
        clearTimeout(_lastNUpdateTimer);
        _lastNUpdateTimer = undefined;
    }

    _updateLastN(store);
}

MiddlewareRegistry.register(store => {
    _followPageVisibility(store);
    setOnScreenTilesListener(() => _scheduleLastNUpdate(store));

    return next => action => {
        const result = next(action);

        switch (action.type) {
        case CONFERENCE_JOINED:
            _lastSelectedSources = undefined;

        // falls through
        case APP_STATE_CHANGED:
        case SET_AUDIO_ONLY:
        case SET_FILMSTRIP_ENABLED:
        case SET_PIP_ACTIVE:
        case SET_TILE_VIEW:
            _flushLastNUpdate(store);
            break;

        case PARTICIPANT_JOINED:
        case PARTICIPANT_KICKED:
        case PARTICIPANT_LEFT:
        case SCREEN_SHARE_REMOTE_PARTICIPANTS_UPDATED:
        case TRACK_ADDED:
        case TRACK_REMOVED:
            _scheduleLastNUpdate(store);
            break;

        case SELECT_LARGE_VIDEO_PARTICIPANT:
            if (action.forceSelection || action.selectionSource === 'pin-change') {
                _flushLastNUpdate(store);
            } else {
                _scheduleLastNUpdate(store);
            }
            break;
        }

        return result;
    };
});

/**
 * Updates the last N value in the conference based on the current state of the redux store.
 *
 * @param {Store} store - The redux store.
 * @private
 * @returns {void}
 */
function _updateLastN({ getState }) {
    const state = getState();
    const { conference } = state['features/base/conference'];
    const { enabled: audioOnly } = state['features/base/audio-only'];
    const { appState } = state['features/background'] || {};
    const { enabled: filmStripEnabled } = state['features/filmstrip'];
    const config = state['features/base/config'];
    const { lastNLimits } = state['features/base/lastn'];
    const participantCount = getParticipantCount(state);
    const { iAmRecorder } = config;

    if (!conference) {
        logger.debug('There is no active conference, not updating last N');

        return;
    }

    // Focus Mode drives the bridge through setReceiverConstraints() with an
    // explicit source list; setLastN() here would overwrite the lastN it carries
    // and let non-host video back in.
    if (shouldRestrictFocusModeView(state)) {
        return;
    }

    // Web: receive only the videos of on-screen tiles, plus the large video in stage view. Not while the conference
    // is in the Picture-in-Picture window: its tiles live in another document, out of the observer's view.
    const onScreen = getOnScreenTiles();
    let onStageSources;
    let selectedSources;

    if (onScreen && !window.documentPictureInPicture?.window) {
        const sourceOf = id => {
            const track = getVideoTrackByParticipant(state, getParticipantById(state, id));

            return track?.local ? undefined : track?.mhTrack?.getSourceName?.();
        };
        const largeVideoId = state['features/large-video']?.participantId;
        const stageSource = largeVideoId && !shouldDisplayTileView(state) ? sourceOf(largeVideoId) : undefined;

        onStageSources = stageSource ? [ stageSource ] : [];
        selectedSources = [ ...new Set([ ...onScreen ].map(sourceOf).concat(onStageSources)) ]
            .filter(Boolean)
            .sort();
    }

    let lastN = typeof config.channelLastN === 'undefined' ? -1 : config.channelLastN;

    // Apply last N limit based on the # of participants and channelLastN settings.
    const limitedLastN = limitLastN(participantCount, lastNLimits);

    if (limitedLastN !== undefined) {
        lastN = lastN === -1 ? limitedLastN : Math.min(limitedLastN, lastN);
    }

    if (typeof appState !== 'undefined' && appState !== 'active') {
        // When PiP is active in background, keep lastN=1 so the large-video
        // participant's remote stream continues to deliver frames.
        // Guard the slice with `|| {}` — on backgrounding this can run before the
        // picture-in-picture reducer state is available, and destructuring an
        // undefined slice throws "Cannot read property 'isPictureInPictureActive'
        // of undefined". Matches the guarded reads elsewhere (LargeVideo, Conference).
        const { isPictureInPictureActive } = state['features/mobile/picture-in-picture'] || {};

        if (isPictureInPictureActive || isLocalVideoTrackDesktop(state)) {
            lastN = 1;
        } else {
            lastN = 0;
        }
    } else if (_pageHidden && !iAmRecorder) {
        // Web page out of sight: nobody is watching remote video, so do not receive or decode it. Audio is not
        // affected by last-N. Restored as soon as the page is visible again.
        lastN = 0;
    } else if (audioOnly) {
        const { remoteScreenShares, tileViewEnabled } = state['features/video-layout'];
        const largeVideoParticipantId = state['features/large-video'].participantId;
        const largeVideoParticipant
            = largeVideoParticipantId ? getParticipantById(state, largeVideoParticipantId) : undefined;

        // Use tileViewEnabled state from redux here instead of determining if client should be in tile
        // view since we make an exception only for screenshare when in audio-only mode. If the user unpins
        // the screenshare, lastN will be set to 0 here. It will be set to 1 if screenshare has been auto pinned.
        if (!tileViewEnabled && largeVideoParticipant && !largeVideoParticipant.local) {
            lastN = (remoteScreenShares || []).includes(largeVideoParticipantId) ? 1 : 0;
        } else {
            lastN = 0;
        }
    } else if (!filmStripEnabled) {
        lastN = 1;
    } else if (selectedSources) {
        lastN = lastN === -1 ? selectedSources.length : Math.min(lastN, selectedSources.length);
    }

    const selectedKey = selectedSources && `${onStageSources}|${selectedSources}`;

    if (conference.getLastN() === lastN && selectedKey === _lastSelectedSources) {
        return;
    }

    //  logger.info(`Setting last N to: ${lastN}`);

    try {
        // One message: setLastN() too would first send the new lastN with the
        // previous selection.
        if (selectedSources) {
            conference.setReceiverConstraints({
                lastN,
                onStageSources,
                selectedSources
            });
        } else {
            conference.setLastN(lastN);
        }
        _lastSelectedSources = selectedKey;
    } catch (err) {
        logger.error(`Failed to set lastN: ${err}`);
    }
}
